import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { buildWaiter } from '@ember/test-waiters';
import { restartableTask } from 'ember-concurrency';
import BaseController from 'uplisting-frontend/pods/base/controller';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import {
  type IMentionable,
  mentionablesFrom,
} from 'uplisting-frontend/utils/team-mentions';

const RESOURCE_TYPE = 'booking';
const waiter = buildWaiter('uplisting-frontend:booking-team-thread');

export default class SidebarBookingsInternalMessagesController extends BaseController {
  @service analytics!: Services['analytics'];
  @service sentry!: Services['sentry'];

  @service('repositories/team-conversation')
  teamConversationRepository!: Services['repositories/team-conversation'];

  @service('repositories/team-message')
  teamMessageRepository!: Services['repositories/team-message'];

  @service('repositories/teammate')
  teammateRepository!: Services['repositories/teammate'];

  @tracked booking!: ModelRegistry['booking'];
  @tracked conversation?: ModelRegistry['team-conversation'];
  @tracked messages: ModelRegistry['team-message'][] = [];
  @tracked isLoading = false;
  @tracked teammates: ModelRegistry['teammate'][] = [];

  get mentionables(): IMentionable[] {
    return mentionablesFrom({
      members: this.conversation?.teamConversationMembers,
      teammates: this.teammates,
      currentUserId: this.session.currentUser?.id,
    });
  }

  loadThread = restartableTask(async (booking: ModelRegistry['booking']) => {
    const token = waiter.beginAsync();

    this.conversation = undefined;
    this.messages = [];
    this.isLoading = true;

    try {
      const conversation = await this.teamConversationRepository.anchoredTo(
        RESOURCE_TYPE,
        booking.id,
      );
      const messages = conversation
        ? await this.teamMessageRepository.forConversation(conversation)
        : [];

      this.conversation = conversation;
      this.messages = messages;
      this.isLoading = false;

      this.analytics.trackEvent(AnalyticsEvents.teamThreadOpened, {
        resourceType: RESOURCE_TYPE,
        hasConversation: Boolean(conversation),
      });

      if (conversation) {
        await this.teamConversationRepository.markRead(conversation);
      }

      await this.loadTeammates();
    } catch (err) {
      this.isLoading = false;
      this.sentry.captureException(err);
      this.notifications.error('team.thread.load_failed');
    } finally {
      waiter.endAsync(token);
    }
  });

  @action
  handlePosted(message: ModelRegistry['team-message']): void {
    this.messages = [...this.messages, message];
    this.conversation ??= message.teamConversation ?? undefined;
  }

  private async loadTeammates(): Promise<void> {
    if (this.teammates.length || !this.session.currentUser?.canListTeammates) {
      return;
    }

    try {
      this.teammates = (await this.teammateRepository.findAll()).slice();
    } catch (err) {
      this.sentry.captureException(err);
    }
  }
}
